import type { LinearFlowNode } from '@vk-hr-tek/ui/FlowTree';
import { AppError } from '@vk-hr-tek/core/error';

import type { EventNodeFlow, NodeAction } from '@app/gen/events';

import { VacationsFlowCommonMapper } from './vacations.flow-common.mapper';

const REJECT_ACTION_TYPE = ['return'];

const CUSTOM_ACTION_TYPE = [
  'system_booking_hook',
  'system_booking_approve',
  'system_booking_trip_create',
  'system_booking_trip_info',
  'system_booking_trip_change',
  'system_booking_trip_ordering',
  'system_booking_limits_exceeded_approve',
  'system_booking_trip_limit_approved',
  'system_booking_trip_cancel',
  'system_linkedtrip_attrinfo',
  'system_linkedtrip_approveinfo',
];

const MAX_FLOW_TRANSITIONS_LENGTH = 10000;

export class VacationsLinearFlowMapper {
  constructor(private vacationsFlowHelpersMapper: VacationsFlowCommonMapper) {}

  private nodes: EventNodeFlow[];

  private constrictions: Set<string>;

  private action: NodeAction;

  private setNodeAction(actions: NodeAction[]): void {
    const action = actions.find(({ is_primary }) => is_primary);

    if (action) {
      this.action = action;
    }
  }

  public init(nodes: EventNodeFlow[]) {
    this.nodes = nodes.map(({ actions, transitions, ...node }) => ({
      ...node,
      actions: actions.filter(({ type }) => !REJECT_ACTION_TYPE.includes(type)),
      transitions: transitions.filter(
        ({ action_type: type }) => !REJECT_ACTION_TYPE.includes(type),
      ),
    }));

    const found = new Set<string>();
    this.constrictions = new Set<string>();

    for (const { transitions } of nodes) {
      for (const { to_node_id: toNodeId } of transitions) {
        if (found.has(toNodeId)) {
          this.constrictions.add(toNodeId);
        } else {
          found.add(toNodeId);
        }
      }
    }
  }

  private createRegularNode(
    node: EventNodeFlow | LinearFlowNode,
    stage: number,
  ): LinearFlowNode {
    if ('node_id' in node) {
      const state = this.vacationsFlowHelpersMapper.mapStatus(node.status);
      const allExecutors =
        state === 'active' ||
        state === 'skipped' ||
        state === 'rejected' ||
        !node.activity_date;

      const executor = allExecutors
        ? this.vacationsFlowHelpersMapper.getExecutorsData(this.action)
        : { position: node.actor?.role, name: node.actor?.fio ?? '' };

      const nodeTitle = node.custom_state?.name
        ? node.custom_state.name
        : this.vacationsFlowHelpersMapper.getNodeTitle(this.action);

      const customActionTitles = CUSTOM_ACTION_TYPE.includes(this.action.type);

      const dataTitle = customActionTitles ? node.name : nodeTitle;

      return {
        nodeId: node.node_id,
        tooltip: this.vacationsFlowHelpersMapper.getTooltip(node, this.action),
        status: node.status,
        title: dataTitle,
        nodeType: 'single',
        executor,
        isOptional: node.is_optional,
        stage,
      };
    } else {
      return node;
    }
  }

  private createStartNode(node: EventNodeFlow, stage: number): LinearFlowNode {
    return {
      nodeId: node.node_id,
      title: 'Старт',
      isOptional: false,
      executor: { position: node.actor?.role ?? '' },
      tooltip: {
        title: 'Старт',
        date: this.vacationsFlowHelpersMapper.dateFormat(node.activity_date),
      },
      nodeType: 'single',
      status: node.status,
      stage,
    };
  }

  private createEndNode(node: EventNodeFlow, stage: number): LinearFlowNode {
    let title: string;

    if (node.custom_state?.name) {
      title = node.custom_state.name;
    } else if (node.name === 'Отказ от подписания') {
      title = node.name;
    } else {
      title = 'Завершено';
    }

    const status =
      node.name === 'Отказ от подписания' ? 'canceled' : node.status;

    return {
      nodeId: node.node_id,
      title,
      isOptional: false,
      nodeType: 'single',
      tooltip: {
        title,
        date: this.vacationsFlowHelpersMapper.dateFormat(node.activity_date),
      },
      status,
      executor: { position: node.actor?.role ?? '' },
      stage,
    };
  }

  private getLinearNodeData(
    node: EventNodeFlow | LinearFlowNode,
    start: boolean,
    stage: number,
  ) {
    if ('nodeId' in node) {
      return {
        ...node,
        stage,
      };
    }

    this.setNodeAction(node.actions);

    if (start) {
      return this.createStartNode(node, stage);
    }

    if (this.action.type === 'system' && !node.transitions?.length) {
      return this.createEndNode(node, stage);
    }

    return this.createRegularNode(node, stage);
  }

  private handleSingleTransition(
    node: EventNodeFlow,
    visited: Set<string>,
    sortedNodes: (EventNodeFlow | LinearFlowNode)[],
  ) {
    node.transitions.forEach((transition) => {
      const nextNode = this.nodes.find(
        ({ node_id }) => node_id === transition.to_node_id,
      );

      if (!nextNode || visited.has(nextNode.node_id)) {
        return;
      }

      this.depthSearch(nextNode, visited, sortedNodes);
    });
  }

  private handleParallelTransitions(
    node: EventNodeFlow,
    visited: Set<string>,
    sortedNodes: (EventNodeFlow | LinearFlowNode)[],
  ) {
    const oneOfTransitionsNode = this.nodes.find(
      ({ node_id }) => node_id === node.transitions[0].to_node_id,
    );

    if (!oneOfTransitionsNode) return;

    const transitionNodesIds = node.transitions.map(
      ({ to_node_id }) => to_node_id,
    );

    const allTransitionsNodes = this.nodes.filter(({ node_id }) =>
      transitionNodesIds.includes(node_id),
    );

    const transitionsNodesStatus = allTransitionsNodes.map(
      ({ status }) => status,
    );

    const oneNodeIsActive = transitionsNodesStatus.includes('active');
    const oneNodeIsFinished = transitionsNodesStatus.includes('finished');
    const oneNodeIsCanceled = transitionsNodesStatus.includes('canceled');
    const oneNodeIsSkipped = transitionsNodesStatus.includes('skipped');

    let parallelNodeStatus:
      | 'finished'
      | 'skipped'
      | 'active'
      | 'canceled'
      | 'inactive' = 'inactive';

    if (oneNodeIsActive) {
      parallelNodeStatus = 'active';
    } else if (oneNodeIsFinished) {
      parallelNodeStatus = 'finished';
    } else if (oneNodeIsCanceled) {
      parallelNodeStatus = 'canceled';
    } else if (oneNodeIsSkipped) {
      parallelNodeStatus = 'skipped';
    }

    const {
      node_id: nodeId,
      is_optional: isOptional,
      transitions,
    } = oneOfTransitionsNode;

    if (!visited.has(nodeId)) {
      sortedNodes.push({
        nodeId,
        status: parallelNodeStatus,
        isOptional,
        title: 'Параллельные этапы',
        stage: 0,
        nodeType: 'parallel',
      });

      allTransitionsNodes.forEach(({ node_id }) => visited.add(node_id));
    }

    const nextNode = this.nodes.find(
      ({ node_id }) => node_id === transitions[0].to_node_id,
    );

    if (!nextNode || visited.has(nextNode.node_id)) {
      return; // Пропускаем, если узел уже посещен
    }

    this.depthSearch(nextNode, visited, sortedNodes);
  }

  private handleForkTransitions(
    node: EventNodeFlow,
    visited: Set<string>,
    sortedNodes: (EventNodeFlow | LinearFlowNode)[],
    transitionNodes: EventNodeFlow[],
  ) {
    const isForkUnactive = transitionNodes.every(
      ({ status }) => status === 'inactive',
    );

    if (isForkUnactive) {
      if (!visited.has(`fork-node-${node.node_id}`)) {
        sortedNodes.push({
          nodeId: `fork-node-${node.node_id}`,
          status: 'inactive',
          isOptional: node.is_optional,
          title: 'Этап с развилкой',
          stage: 0,
          nodeType: 'fork',
        });
      }
    } else {
      const neededStatuses = ['active', 'skipped', 'finished'];

      const forkActiveNode = transitionNodes.find(({ status }) =>
        neededStatuses.includes(status),
      );

      if (!forkActiveNode) {
        return;
      }

      const forkActiveNodeTransitions = forkActiveNode?.transitions.map(
        ({ to_node_id }) => to_node_id,
      );

      const transitionsNodeAfterForkNode = this.nodes.filter(({ node_id }) =>
        forkActiveNodeTransitions?.includes(node_id),
      );

      if (transitionsNodeAfterForkNode.length > 1) {
        visited.add(node.node_id);
        sortedNodes.push(forkActiveNode);

        this.depthSearch(forkActiveNode, visited, sortedNodes);
      }

      const activeNodeAfterFork = transitionsNodeAfterForkNode.find(
        ({ status }) => neededStatuses.includes(status),
      );

      const forkWithInactivesNodes =
        !!transitionsNodeAfterForkNode.length &&
        transitionsNodeAfterForkNode.every(
          ({ status }) => status === 'inactive',
        );

      if (!visited.has(forkActiveNode.node_id)) {
        visited.add(forkActiveNode.node_id);
        sortedNodes.push(forkActiveNode);
      }

      if (forkWithInactivesNodes) {
        sortedNodes.push({
          nodeId: `fork-node-${node.node_id}`,
          status: 'inactive',
          isOptional: node.is_optional,
          title: 'Этап с развилкой',
          stage: 0,
          nodeType: 'fork',
        });

        return;
      }

      if (activeNodeAfterFork && !visited.has(activeNodeAfterFork.node_id)) {
        this.depthSearch(activeNodeAfterFork, visited, sortedNodes);
      }
    }
  }

  // Рекурсивный обход узлов (depthSearch)
  private depthSearch(
    node: EventNodeFlow,
    visited: Set<string>,
    sortedNodes: (EventNodeFlow | LinearFlowNode)[],
  ) {
    if (!node || visited.has(node.node_id)) return;

    visited.add(node.node_id);
    sortedNodes.push(node);

    if (node.transitions.length > 1) {
      const idsForTransition = node.transitions.map(
        ({ to_node_id }) => to_node_id,
      );

      const transitionsNodes = this.nodes.filter(({ node_id }) =>
        idsForTransition.includes(node_id),
      );

      const isParallelNode = node.actions.length === 1;

      if (isParallelNode) {
        this.handleParallelTransitions(node, visited, sortedNodes);
      } else {
        this.handleForkTransitions(
          node,
          visited,
          sortedNodes,
          transitionsNodes,
        );
      }
    } else {
      this.handleSingleTransition(node, visited, sortedNodes);
    }
  }

  private sortNodesByTransitions(startNodeId: string) {
    const sortedNodes: (EventNodeFlow | LinearFlowNode)[] = [];
    const visited: Set<string> = new Set();

    if (this.nodes.length > MAX_FLOW_TRANSITIONS_LENGTH) {
      throw new AppError('client', {
        code: 400,
        message: 'Ошибка данных в linearFlow.mapper',
        error: 'Ошибка данных в linearFlow.mapper',
      });
    }

    const startNode = this.nodes.find(({ node_id }) => node_id === startNodeId);

    if (!startNode) return;

    this.depthSearch(startNode, visited, sortedNodes);

    return sortedNodes;
  }

  public getLinearFlowTree(startId: string): LinearFlowNode[] {
    const sortedNodes = this.sortNodesByTransitions(startId);

    const uniqueNodes = sortedNodes?.filter(
      (node, index, array) =>
        index ===
        array.findIndex(
          (n) =>
            ('nodeId' in n ? n.nodeId : n.node_id) ===
            ('nodeId' in node ? node.nodeId : node.node_id),
        ),
    );

    if (!uniqueNodes) return [];

    const isCanceledNode = uniqueNodes.some(
      ({ status }) => status === 'canceled',
    );

    if (isCanceledNode) {
      const cancelNodeIndex = uniqueNodes.findIndex(
        ({ status }) => status === 'canceled',
      );

      const sliceSortedNodes = uniqueNodes.slice(0, cancelNodeIndex + 1);

      return sliceSortedNodes.map((node, index) =>
        this.getLinearNodeData(node, !index, index + 1),
      );
    }

    return uniqueNodes.map((node, index) =>
      this.getLinearNodeData(node, !index, index + 1),
    );
  }
}
